import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useAuthHttp } from '@hooks';
import { MASTER_DATA_IMPORT_LIST } from '@constants';
import { showAiFeaturesAtom } from '../../store/flags';
import { ApiImportListItem, ImportEntityType } from './importApi';

const OPEN_STATUSES = ['PendingAnalysis', 'Analysed', 'AnalysisFailed'];

export function PendingImportsBanner({
  entityType,
  refreshKey = 0,
}: {
  entityType: ImportEntityType;
  refreshKey?: number;
}) {
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const [openImports, setOpenImports] = useState<ApiImportListItem[]>([]);

  useEffect(() => {
    if (!showAiFeatures) return;
    let isActive = true;
    const fetchOpenImports = async () => {
      try {
        const { data } = await authHttp.post(MASTER_DATA_IMPORT_LIST, {
          entityType,
          statuses: OPEN_STATUSES,
          pagination: { itemsPerPage: 50 },
        });
        if (isActive) setOpenImports(data.items ?? []);
      } catch {
        /* the banner is best-effort — the master page works without it */
      }
    };
    fetchOpenImports();
    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAiFeatures, entityType, refreshKey]);

  if (!showAiFeatures || openImports.length === 0) return null;

  const newestImport = openImports[0];

  return (
    <div className='mx-8 mt-4 flex items-center justify-between rounded-md bg-blue-50 px-4 py-3'>
      <div className='flex items-center gap-2'>
        <SparklesIcon className='h-5 w-5 text-primary' />
        <span className='font-inter text-sm text-gray-700'>
          {openImports.length} import{openImports.length === 1 ? '' : 's'}{' '}
          awaiting review
        </span>
      </div>
      <button
        type='button'
        onClick={() => navigate(`/master-data/imports/${newestImport.id}`)}
        className='font-inter text-sm font-semibold text-primary hover:underline'
      >
        Review {newestImport.fileName} →
      </button>
    </div>
  );
}
